import axios from 'axios';
import { getCookie, setCookie } from '../cookies';
import { CartData, processCartData, reSyncCart, serverCartData, ShopifyCartData } from '.';
import { CartFragmentFragment } from './cart-api-queries-mutations/CartFragment.shopify.generated';
import {
  addLineItemToCart,
  applyPromoCodeToCart,
  CartItemAttributes,
  CartItemProperties,
  fetchCart,
  LineItem,
  LineItems,
} from './cart-apis';
import { transformCartApiResponse } from './transform-cart-api-response';

import { CartLineFragmentFragment } from './cart-api-queries-mutations/CartLine.shopify.generated';
import { CartLineVariantFragment } from './cart-api-queries-mutations/CartLineVariantFragment.shopify.generated';
import { runLocationBasedGlobalFunctions } from 'src/scripts/global';

const CURRENT_CART_DATA = 'currentCartDetails';
const CART_TYPE = 'cart-api';

export const findLineItemByVariantId = (variantIds: string[], cart: CartData) => {
  const regularItems = cart.regularItems
    .filter((item) => variantIds.includes(String(item.variant_id)))
    .map((i) => i.line_id);
  if (regularItems.length) return regularItems;
  const giftItems = cart.giftItemsAndLoyaltyRewards
    .filter((item) => variantIds.includes(String(item.variant_id)))
    .map((i) => i.line_id);
  return giftItems;
};

export const extractShopifyId = (id: string | null): string => {
  if (!id || typeof id !== 'string') {
    return '';
  }
  const match = id.match(/(\d+)$/);
  return match?.[1] ?? '';
};

export const getDisplayPriceCart = (amount: string | undefined) => {
  if (amount === undefined) return undefined;
  const displayPrice = Number(amount) * 100;
  return displayPrice;
};

export const getTotalDiscountAmount = (input: CartFragmentFragment | undefined | null) => {
  const totalCartDiscount =
    input?.discountAllocations.reduce((total, item) => total + item?.discountedAmount?.amount, 0) ?? 0;
  let productLevelDiscounts = 0;
  input?.lines.edges.forEach((item) => {
    productLevelDiscounts += getLineItemDiscountPrice(item);
  });
  return totalCartDiscount + productLevelDiscounts;
};

export const getCurrentCartData = async () => {
  const cartData = await fetchCart();
  const warnings = cartData && 'warnings' in cartData ? cartData?.warnings : [];
  const transformedCartData = transformCartApiResponse(cartData?.cart, { warnings });
  serverCartData.cart = transformedCartData;
  runLocationBasedGlobalFunctions();
  await reSyncCart(transformedCartData, { forceUpdateSpecialItems: true });
};

export const getLineItemDiscountPrice = (input: CartLineFragmentFragment) => {
  const amount: number = input.node.discountAllocations.reduce((sum, discount) => {
    return sum + parseFloat(discount.discountedAmount.amount);
  }, 0);
  return Number(amount) * 100;
};

export const getCollectionArray = (input: CartLineVariantFragment) => {
  const collections: string[] = input.product.collections.nodes.map((item) => item.handle);
  return collections;
};

export const transformCartItemAttributesToFitCartLinesAddMutation = (cartItemProperties: CartItemProperties) => {
  const cartItemAttributes = Object.entries(cartItemProperties)
    .filter(([key, value]) => value !== undefined && (typeof value !== 'string' || value !== ''))
    .map(([key, value]) => ({
      key,
      value: String(value),
    }));
  return cartItemAttributes;
};

export const storeCartDataToLocalStore = (data: ShopifyCartData) => {
  try {
    const cartData = processCartData(data);
    window.localStorage.setItem(CURRENT_CART_DATA, btoa(encodeURIComponent(JSON.stringify(cartData))));
  } catch (error) {
    console.log(error);
  }
};

export const getCartDataFromLocalStore = (): CartData | null => {
  try {
    const storedCartDetails = JSON.parse(
      decodeURIComponent(atob(window.localStorage.getItem(CURRENT_CART_DATA) || '') || '{}')
    );
    if (Object.keys(storedCartDetails).length) return storedCartDetails;
    else return null;
  } catch (err) {
    console.log('Local data fetch failed.', err);
    return null;
  }
};

export const syncAjaxCartToCartApi = () => {
  const cartType = getCookie('cart_type');
  const lines: LineItems = [];
  if (cartType !== CART_TYPE) {
    axios.get<ShopifyCartData>(`/cart.js`).then(async ({ data: ajaxCartData }) => {
      setCookie('cart_type', CART_TYPE);
      if (ajaxCartData.items.length) {
        ajaxCartData.items?.forEach((item) => {
          let updates = {} as LineItem;
          let cartItemAttributes: CartItemAttributes[] = [];
          updates.merchandiseId = item.id;
          updates.quantity = item.quantity;
          if (item.properties && typeof item.properties === 'object' && Object.entries(item.properties).length) {
            cartItemAttributes = Object.entries(item.properties)
              .filter(([key, value]) => value !== undefined)
              .map(([key, value]) => ({
                key,
                value: String(value),
              }));
          }
          if (cartItemAttributes.length) {
            updates.cartItemAttributes = [...cartItemAttributes];
          }
          lines.push(updates);
        });
        addLineItemToCart(lines).then((response) => {
          const cartApiData = transformCartApiResponse(response?.cart, { warnings: response?.warnings });
          reSyncCart(cartApiData);
        });
        console.log('Synced AJAX cart to Cart API');
      }
    });
  }
};

export const autoApplyPromoCodeOnLogin = async (discountCode: string) => {
  const cartWithPromo = await applyPromoCodeToCart(discountCode);
  const newCartData = transformCartApiResponse(cartWithPromo?.cart, { warnings: cartWithPromo?.warnings });
  await reSyncCart(newCartData);
};

export const generateStorefrontCookie = (ajaxCart: string) => {
  return ajaxCart ? `gid://shopify/Cart/${ajaxCart}` : '';
};

export const generateAjaxCartCookie = (storefrontCart: string) => {
  return storefrontCart.replace('gid://shopify/Cart/', '');
};

export const splitAtFirstColon = (str: string) => {
  const index = str.indexOf(':');
  if (index === -1) return [str];
  return [str.slice(0, index), str.slice(index + 1)];
};

export const transformArrayToObject = (inputArray: string[]): string[] => {
  return inputArray.map((item) => item.substring(item.indexOf(':') + 1).trim());
};
